// Interiors in two modes (decision 0048: "the engine supports both seamless interiors and separate scenes with a
// transition; spike 002 uses seamless"). Built in spike 002 C4.
//
// - **Seamless:** the interior is part of its building, in place. Walking into its room makes it the active interior;
//   the game then switches the camera to the interior's profile, cuts away the roof and near walls, and asks its look
//   for an indoor light model. Nothing loads: the room was always there.
// - **Transition:** a door that leads elsewhere in the same world (a room built away from the town, a cellar under it).
//   Walking into the door's trigger fades out, moves the player to the door's destination at full black, and fades
//   back in. The destination is usually inside another interior, which then becomes active the same way as above.
//
// Both are one director: interiors are oriented boxes (whichever holds the player is active, with a margin before it
// lets go), doors are triggers with a destination. `step` is deterministic and runs in the sim (the warp moves the
// player, so it must replay); its state is JSON and goes in the game's save. The camera, the cutaway and the light are
// the game's to apply from the events and the state; the fade's opacity is `fade()`.

/** A point. */
export interface WorldPoint {
  x: number;
  y: number;
  z: number;
}

/** An oriented box: centre, half extents, and a yaw about +y (three.js's `rotation.y`: local +x maps to world
 *  (cos yaw, 0, −sin yaw)). */
export interface WorldBox {
  x: number;
  y: number;
  z: number;
  hx: number;
  hy: number;
  hz: number;
  yaw: number;
}

/** An interior: the room's box, how it is entered, and what the game switches to inside. */
export interface InteriorSpec {
  id: string;
  /** Seamless (in place, walked into) or transition (reached through a door that fades). */
  mode: "seamless" | "transition";
  bounds: WorldBox;
  /** The camera profile to use inside. */
  profile: string;
  /** The indoor light model the game's look should use inside (an ID the look knows). */
  light: string;
}

/** A transition door: stand within `radius` of it and it fades and moves the player to `to`. */
export interface TransitionDoor {
  id: string;
  at: WorldPoint;
  radius: number;
  /** The destination, and the facing to arrive with (rad). */
  to: WorldPoint & { yaw: number };
}

/** The director's numbers (the game's). */
export interface InteriorsOptions {
  /** How far (m) outside an active interior's box the player may go before it lets go (no flicker at the door). */
  exitMargin: number;
  /** Seconds of fade out before the move, and of fade in after it. */
  fadeOut: number;
  fadeIn: number;
}

/** A transition in progress. */
export interface DoorTransit {
  door: string;
  phase: "out" | "in";
  /** Seconds into the phase. */
  t: number;
}

/** The director's state (JSON-safe; save it with the game). */
export interface InteriorsState {
  /** The active interior's ID, or null outdoors. */
  inside: string | null;
  transit: DoorTransit | null;
  /** Doors trigger only after the player has stood clear of every door once (so arriving next to the way back doesn't
   *  bounce them out again). */
  armed: boolean;
  /** Counts since the start: entries, exits and door transits completed. */
  enters: number;
  exits: number;
  transits: number;
}

/** What happened on a step. */
export type InteriorEvent =
  | { type: "enter"; interior: InteriorSpec }
  | { type: "exit"; interior: InteriorSpec }
  /** A door started its fade out. */
  | { type: "door"; door: TransitionDoor }
  /** At full black: move the player here now. */
  | { type: "warp"; door: TransitionDoor; to: WorldPoint & { yaw: number } }
  /** The fade in finished. */
  | { type: "arrived"; door: TransitionDoor };

/** Planted defects (tests). */
export interface InteriorsFaults {
  /** A door fades out and back in but never moves the player (the transition test must go red). */
  noWarp: boolean;
  /** Interiors are never entered (the game's interior checks must go red). */
  neverEnter: boolean;
}

/** Whether a point is inside a box grown by `margin` on every side. */
export function boxContains(b: WorldBox, p: WorldPoint, margin = 0): boolean {
  const dx = p.x - b.x;
  const dz = p.z - b.z;
  const c = Math.cos(b.yaw);
  const s = Math.sin(b.yaw);
  // world → local: the inverse of local → world (x cos + z sin, −x sin + z cos)
  const lx = dx * c - dz * s;
  const lz = dx * s + dz * c;
  return Math.abs(lx) <= b.hx + margin && Math.abs(lz) <= b.hz + margin && Math.abs(p.y - b.y) <= b.hy + margin;
}

/** A fresh state. */
export function initialInteriors(): InteriorsState {
  return { inside: null, transit: null, armed: true, enters: 0, exits: 0, transits: 0 };
}

/** The interiors director. */
export class Interiors {
  readonly faults: InteriorsFaults = { noWarp: false, neverEnter: false };
  private readonly byId = new Map<string, InteriorSpec>();
  private readonly doors: readonly TransitionDoor[];
  private s: InteriorsState;

  constructor(
    spec: { interiors: readonly InteriorSpec[]; doors?: readonly TransitionDoor[] },
    private readonly o: InteriorsOptions,
    state: InteriorsState = initialInteriors(),
  ) {
    for (const i of spec.interiors) {
      if (this.byId.has(i.id)) throw new Error(`interiors: "${i.id}" twice`);
      if (!(i.bounds.hx > 0 && i.bounds.hy > 0 && i.bounds.hz > 0)) throw new Error(`interiors: "${i.id}" has an empty box`);
      this.byId.set(i.id, i);
    }
    const ids = new Set<string>();
    for (const d of spec.doors ?? []) {
      if (ids.has(d.id)) throw new Error(`interiors: door "${d.id}" twice`);
      if (!(d.radius > 0)) throw new Error(`interiors: door "${d.id}" needs a radius > 0`);
      ids.add(d.id);
    }
    this.doors = spec.doors ?? [];
    if (!(o.fadeOut >= 0 && o.fadeIn >= 0 && o.exitMargin >= 0)) throw new Error("interiors: fades and margin must be >= 0");
    this.s = structuredClone(state);
  }

  /** Every interior. */
  interiors(): InteriorSpec[] {
    return [...this.byId.values()];
  }

  /** An interior by ID. */
  get(id: string): InteriorSpec | undefined {
    return this.byId.get(id);
  }

  /** The active interior, or null. */
  active(): InteriorSpec | null {
    return this.s.inside ? (this.byId.get(this.s.inside) ?? null) : null;
  }

  /** The first interior whose box holds `p` (no margin), or null. */
  containing(p: WorldPoint): InteriorSpec | null {
    for (const i of this.byId.values()) if (boxContains(i.bounds, p)) return i;
    return null;
  }

  /** Whether a door transition is running (the game holds the player's input meanwhile). */
  busy(): boolean {
    return this.s.transit !== null;
  }

  /** The fade's opacity now: 0 clear, 1 black. */
  fade(): number {
    const t = this.s.transit;
    if (!t) return 0;
    if (t.phase === "out") return this.o.fadeOut > 0 ? Math.min(1, t.t / this.o.fadeOut) : 1;
    return this.o.fadeIn > 0 ? Math.max(0, 1 - t.t / this.o.fadeIn) : 0;
  }

  /** One sim step with the player at `p`. Returns what happened, in order. */
  step(p: WorldPoint, dt: number): InteriorEvent[] {
    const out: InteriorEvent[] = [];
    const s = this.s;
    if (s.transit) {
      const door = this.doors.find((d) => d.id === s.transit!.door);
      s.transit.t += dt;
      if (!door) s.transit = null;
      else if (s.transit.phase === "out" && s.transit.t >= this.o.fadeOut - 1e-9) {
        if (!this.faults.noWarp) out.push({ type: "warp", door, to: { ...door.to } });
        s.transit = { door: door.id, phase: "in", t: 0 };
        // the player is moved by the game now: the rooms are judged from the next step's position
        return out;
      } else if (s.transit.phase === "in" && s.transit.t >= this.o.fadeIn - 1e-9) {
        s.transit = null;
        s.transits++;
        out.push({ type: "arrived", door });
      }
    } else {
      const near = this.doors.find((d) => Math.hypot(p.x - d.at.x, p.z - d.at.z) <= d.radius && Math.abs(p.y - d.at.y) <= 2);
      if (near && s.armed) {
        s.armed = false;
        s.transit = { door: near.id, phase: "out", t: 0 };
        out.push({ type: "door", door: near });
      } else if (!near) s.armed = true;
    }
    // the rooms: let go of the active one past its margin, then take the one that holds the player
    const cur = this.active();
    if (cur && !boxContains(cur.bounds, p, this.o.exitMargin)) {
      s.inside = null;
      s.exits++;
      out.push({ type: "exit", interior: cur });
    }
    if (!s.inside && !this.faults.neverEnter) {
      const next = this.containing(p);
      if (next) {
        s.inside = next.id;
        s.enters++;
        out.push({ type: "enter", interior: next });
      }
    }
    return out;
  }

  /** The state (a copy, JSON-safe). */
  state(): InteriorsState {
    return structuredClone(this.s);
  }

  /** Put back a saved state. */
  restore(s: InteriorsState): void {
    if (s.inside !== null && !this.byId.has(s.inside)) throw new Error(`interiors: restore names an unknown interior "${s.inside}"`);
    this.s = structuredClone(s);
  }
}
